/**
 * Failed Payment Trigger Component
 *
 * Trigger: "failed a payment"
 * Shows: sellable item select, purchase type multi-select, Stripe plan options
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { fetchSellables } from 'src/models/payment/payment';
import { SellableSelect } from 'src/shared/Forms/Selects/SellableSelect';

import { TriggerComponentProps } from '../automationTriggers';
import { StripePlanFields } from '../components/StripePlanFields';

interface SellableValue {
  id: number | string;
  type: string;
  name: string;
}

const PURCHASE_TYPE_OPTIONS = [
  { value: 'new_subscriptions', labelKey: 'newSubscription' },
  { value: 'existing_subscription', labelKey: 'existingSubscription' },
  { value: 'one_time_purchase', labelKey: 'oneTimePurchaseType' },
] as const;

export const FailedPaymentTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const { data: sellables = [] } = useQuery({
    queryKey: ['sellables'],
    queryFn: () => fetchSellables(),
    staleTime: 60000,
  });

  const selectedSellable = sellables.find(
    (item) =>
      String(item.id) === String(automation.reminderTypeId) &&
      item.type === automation.reminderTypeClass
  );

  const purchaseTypeValues = Array.isArray(automation.lifeTime)
    ? automation.lifeTime
    : [];

  const purchaseTypeOptions = PURCHASE_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: t(`automations.purchase.${option.labelKey}`),
  }));

  const selectedPurchaseTypes = purchaseTypeOptions.filter((option) =>
    purchaseTypeValues.includes(option.value)
  );

  if (readOnly) {
    const parts: string[] = [t('automations.triggers.failedPayment')];

    if (selectedSellable?.name) {
      parts.push(`- ${selectedSellable.name}`);
    } else if (automation.paymentPlanName) {
      parts.push(`- ${automation.paymentPlanName}`);
    }

    if (selectedPurchaseTypes.length > 0) {
      parts.push(
        `(${selectedPurchaseTypes.map((option) => option.label).join(', ')})`
      );
    }

    return <span>{parts.join(' ')}</span>;
  }

  const sellableValue: SellableValue | null = selectedSellable
    ? {
        id: selectedSellable.id,
        type: selectedSellable.type,
        name: selectedSellable.name,
      }
    : null;

  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 400 }}>
        <SellableSelect
          label={t('automations.failedPayment.selectSellableItem')}
          placeholder={t('automations.failedPayment.anyItem')}
          value={sellableValue as Parameters<typeof SellableSelect>[0]['value']}
          valueOnly={false}
          onChange={(value) => {
            const sellable = value as SellableValue | null;
            setFieldValue(
              `automations.${index}.reminderTypeId`,
              sellable?.id ?? null
            );
            setFieldValue(
              `automations.${index}.reminderTypeClass`,
              sellable?.type ?? null
            );
            setFieldValue(`automations.${index}.stripePlan`, null);
            setFieldValue(`automations.${index}.afterTrial`, 0);
          }}
        />
      </Box>
      <Box sx={{ maxWidth: 400 }}>
        <Autocomplete
          multiple
          options={purchaseTypeOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedPurchaseTypes}
          onChange={(_, newValue) => {
            setFieldValue(
              `automations.${index}.lifeTime`,
              newValue.map((option) => option.value)
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('automations.failedPayment.triggerCondition')}
              placeholder={t('automations.failedPayment.anyPurchaseType')}
              size="small"
            />
          )}
        />
      </Box>
      <StripePlanFields automation={automation} index={index} />
    </Stack>
  );
};
